import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { Button } from './Button';
import { cn } from '@/lib/cn';

/*
  Modal dialog.

  Built on Radix rather than hand-rolled, for the same reasons the navigation drawer in
  AppShell is: focus moves into the dialog on open and is trapped there, Escape closes it,
  focus returns to whatever opened it, the rest of the page is marked aria-hidden, and the
  scroll lock does not shift the layout. Every one of those is a thing hand-rolled dialogs
  get wrong, and three of them are invisible until someone tries the keyboard.

  Two rules this wrapper enforces rather than leaving to each caller:

  * A title and a description are required props, not optional children. Radix warns when
    aria-describedby points at nothing, and more to the point a confirmation dialog with no
    description is a dialog that has not said what it is about to do.
  * The close button is always present and always has an accessible name. A dialog that can
    only be dismissed with Escape is unusable with a switch device or a head pointer.

  `onOpenChange` is deliberately not intercepted. A caller that needs to block dismissal
  while a mutation is in flight passes `busy`, which disables the close affordances and the
  outside-click and Escape handlers, so a half-finished write cannot be abandoned into an
  unknown state by a stray Escape keypress.
*/

export type DialogWidth = 'sm' | 'md' | 'lg';

const WIDTH_CLASSES: Readonly<Record<DialogWidth, string>> = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-3xl',
};

export interface DialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly description: React.ReactNode;
  readonly children: React.ReactNode;
  /** Rendered in the sticky footer. Put the primary action last, as the platform does. */
  readonly footer?: React.ReactNode;
  readonly width?: DialogWidth;
  /** While true, Escape and outside clicks do not close. Used during a write. */
  readonly busy?: boolean;
}

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  width = 'md',
  busy = false,
}: DialogProps): React.JSX.Element {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="animate-fade-in fixed inset-0 z-40 bg-[var(--color-overlay)]" />
        <RadixDialog.Content
          onEscapeKeyDown={(event) => {
            if (busy) {
              event.preventDefault();
            }
          }}
          onPointerDownOutside={(event) => {
            if (busy) {
              event.preventDefault();
            }
          }}
          onInteractOutside={(event) => {
            if (busy) {
              event.preventDefault();
            }
          }}
          className={cn(
            'animate-pop-in fixed top-1/2 left-1/2 z-50 flex max-h-[90dvh] w-[calc(100vw-2rem)]',
            '-translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden outline-none',
            'rounded-[var(--radius-panel)] border border-border bg-surface shadow-lg',
            WIDTH_CLASSES[width],
          )}
        >
          <div className="flex items-start justify-between gap-4 border-b border-border px-4 py-3">
            <div className="min-w-0">
              <RadixDialog.Title className="text-sm font-semibold text-fg">
                {title}
              </RadixDialog.Title>
              <RadixDialog.Description className="mt-0.5 text-xs leading-relaxed text-fg-muted">
                {description}
              </RadixDialog.Description>
            </div>
            <RadixDialog.Close asChild>
              <Button variant="ghost" size="sm" aria-label="Close dialog" disabled={busy}>
                <X className="size-4" aria-hidden="true" />
              </Button>
            </RadixDialog.Close>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">{children}</div>

          {footer === undefined ? null : (
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border bg-surface-sunken px-4 py-3">
              {footer}
            </div>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

/**
 * Close button for a dialog footer. Exported so a caller does not have to import Radix to
 * get a Cancel that actually closes the dialog it is inside.
 */
export function DialogCancel({
  children = 'Cancel',
  disabled = false,
}: {
  readonly children?: React.ReactNode;
  readonly disabled?: boolean;
}): React.JSX.Element {
  return (
    <RadixDialog.Close asChild>
      <Button variant="secondary" disabled={disabled}>
        {children}
      </Button>
    </RadixDialog.Close>
  );
}
